import React, { useEffect } from 'react';
import { Modal, ScrollView, StyleSheet } from 'react-native';
import {
  Iffy,
  DNABox,
  DNAHeader,
  DNAText,
  DNACard,
  DISCLAIMER_MODE,
  DNAIcon,
  DNAButton,
  Disclaimer,
  useDNASlider,
  withToastProvider,
} from '@alucio/lux-ui';
import { Divider } from '@ui-kitten/components';

import DNARouteStack, {
  useDNARouteStack,
} from 'src/components/DNA/Navigation/DNARouteStack/DNARouteStack';
import DNARouteTabs from 'src/components/DNA/Navigation/DNARouteTabs/DNARouteTabs';
import Password, { usePasswordForm, PasswordProvider } from './Password';
import { useHistory } from 'src/router';
import useCurrentPage from 'src/components/DNA/hooks/useCurrentPage';
import { ROUTES } from 'src/router/routeDef';
import Settings from './Settings';

import { useAppSettings, useOnlineStatus } from 'src/state/context/AppSettings';
import Offline from './Offline';
import DNAEmpty, { EmptyVariant } from 'src/components/DNA/Empty/DNAEmpty'

export type DisclaimerType = { isVisble: boolean; mode: DISCLAIMER_MODE };

const styles = StyleSheet.create({
  desktopTabsWrapper: {
    paddingBottom: 40,
    paddingLeft: 32,
    paddingRight: 32,
    paddingTop: 8,
  },
  desktopPageWrapper: {
    maxWidth: 1440,
    paddingHorizontal: 48,
    paddingVertical: 20,
  },
});

const ProfileDesktop = () => {
  const isOnline: boolean = useOnlineStatus();

  return (
    <DNABox alignX="center">
      <DNABox fill style={styles.desktopPageWrapper}>
        <DNABox fill spacing="medium" appearance="col">
          {/* Header */}
          <DNABox alignY="center" spacing="between">
            {/* @Kenny, what status was intended here? Secondary does not exist for this component */}
            <DNAText h5 status="secondary">
              Account Profile
            </DNAText>
          </DNABox>
          {/* Content */}
          <DNACard style={styles.desktopTabsWrapper}>
            <DNARouteTabs.Router>
              <DNARouteTabs.Tabs>
                <DNARouteTabs.Tab title="Password" path="/profile/password">
                  {isOnline ? <Password /> : <OfflinePasswordMessage />}
                </DNARouteTabs.Tab>
                <DNARouteTabs.Tab title="Beacon App" path="/profile/offline">
                  <Offline />
                </DNARouteTabs.Tab>
                <DNARouteTabs.Unmatched rewrite="/profile/password" />
              </DNARouteTabs.Tabs>
            </DNARouteTabs.Router>
          </DNACard>
        </DNABox>
      </DNABox>
    </DNABox>
  );
};

const OfflinePasswordMessage: React.FC = () => {
  return (
    <DNABox style={{ height: 512 }}>
      <DNAEmpty
        emptyVariant={EmptyVariant.NoNetworkGenericUnavailable}
      />
    </DNABox>
  );
};

const OfflineMessage: React.FC = () => {
  const stack = useDNARouteStack();

  return (
    <Modal transparent visible>
      <DNABox
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
        appearance="col"
        alignY="center"
        fill
        spacing="around"
      >
        <DNACard style={{ borderRadius: 8, marginHorizontal: 97 }}>
          <DNABox spacing="between" style={{ height: 64 }} alignY="center">
            <DNABox style={{ marginLeft: 20 }}>
              <DNAIcon.Styled
                name="wifi-off"
                appearance="ghost"
                status="subtle"
              />
              <DNAText bold style={{ marginLeft: 21 }}>
                Looks like you’re offline.
              </DNAText>
            </DNABox>
            <DNAButton
              status="primary"
              appearance="ghost"
              context="minimum"
              style={{ marginRight: 20 }}
              onPress={() => stack.push('/profile')}
            >
              Close
            </DNAButton>
          </DNABox>
          <Divider />
          <DNABox alignY="center" style={{ marginLeft: 24, height: 84 }}>
            <DNAText>
              Please connect to the Internet to access this feature.
            </DNAText>
          </DNABox>
        </DNACard>
      </DNABox>
    </Modal>
  );
};

const DNAProfileHeaderTablet: React.FC = () => {
  const stack = useDNARouteStack();
  const slider = useDNASlider();
  const passwordForm = usePasswordForm();

  const isPasswordPage = stack.isCurrentPath('/profile/password', true);
  const headerTitle = isPasswordPage ? 'Change Password' : 'Account Profile';

  return (
    <DNAHeader status="brand" title={headerTitle}>
      <DNAHeader.Left>
        <Iffy is={!isPasswordPage}>
          <DNAButton
            status="brandAlt"
            onPress={() => {
              slider.setVisible(false);
            }}
            testID="tablet-header-close"
          >
            Close
          </DNAButton>
        </Iffy>
        <Iffy is={isPasswordPage}>
          <DNAButton
            status="brandAlt"
            disabled={passwordForm.changingInProgress}
            onPress={() => {
              passwordForm.clearFields();
              stack.pop();
            }}
            testID="tablet-header-close"
          >
            Cancel
          </DNAButton>
        </Iffy>
      </DNAHeader.Left>

      <DNAHeader.Right>
        <Iffy is={isPasswordPage}>
          <DNAButton
            disabled={!passwordForm.isPasswordValid}
            onPress={() => {
              passwordForm.onChangePassword().then((err) => {
                if (!err) stack.pop();
              });
            }}
            testID="change-password-button"
          >
            Change Password
          </DNAButton>
        </Iffy>
      </DNAHeader.Right>
    </DNAHeader>
  );
};

export const TabletProfile: React.FC = () => {
  const currentPage = useCurrentPage({ exact: false });
  const history = useHistory();
  const isOnline: boolean = useOnlineStatus();

  const isProfileScreen = currentPage === ROUTES.PROFILE;

  // Tablet doesn't use the profile route, but a Slider, so rewrite the path if
  //  a user somehow got here
  // [TODO]: Ideally we should enable the profile slider but the state is managed within the Profile Menu
  useEffect(() => {
    if (isProfileScreen) history.replace('/');
  }, [currentPage]);

  if (isProfileScreen) {
    return null;
  }

  return (
    <DNABox fill appearance="col" style={{ backgroundColor: 'white' }}>
      <ScrollView>
        <PasswordProvider>
          <DNARouteStack.Internal initialPath="/profile">
            {/* Header */}
            <DNARouteStack.Header>
              <DNAProfileHeaderTablet />
            </DNARouteStack.Header>

            {/* Settings Page */}
            <DNARouteStack.Screen path="/profile">
              <Settings />
            </DNARouteStack.Screen>

            {/* Password Page */}
            <DNARouteStack.Screen
              path="/profile/password"
              style={{ height: '100vh' }}
            >
              {isOnline ? <Password /> : <OfflinePasswordMessage />}
            </DNARouteStack.Screen>

            {/* Password Offline Page */}
            <DNARouteStack.Screen
              style={{ backgroundColor: 'transparent' }}
              path="/profile/offline"
              skipAnimation
            >
              <OfflineMessage />
            </DNARouteStack.Screen>

            {/* Privacy Disclaimer Page */}
            <DNARouteStack.Screen path="/profile/privacy" skipAnimation>
              {(stack) => (
                <Disclaimer
                  mode={DISCLAIMER_MODE.Privacy}
                  onClose={() => stack.pop()}
                />
              )}
            </DNARouteStack.Screen>

            {/* TOU Disclaimer Page */}
            <DNARouteStack.Screen path="/profile/tou" skipAnimation>
              {(stack) => (
                <Disclaimer
                  mode={DISCLAIMER_MODE.ToU}
                  onClose={() => stack.pop()}
                />
              )}
            </DNARouteStack.Screen>
          </DNARouteStack.Internal>
        </PasswordProvider>
      </ScrollView>
    </DNABox>
  );
};

const componentWrapper = (ChildComponent) => () => <ChildComponent />;
const TabletToasterProfile = componentWrapper(withToastProvider(TabletProfile));

const Profile = (props) => {
  const { deviceMode } = useAppSettings();

  return deviceMode === 'desktop' ? (
    <ProfileDesktop {...props} />
  ) : (
    <TabletToasterProfile {...props} />
  );
};

export default Profile;
